import { ReactNode, useCallback, useMemo } from 'react'

import { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import { ContragentResponse } from '@synapex/shared2/gateway-rest-api'
import { WithOptional } from '@synapex/tools/typescript'

import { useCreateContragent, useUpdateContragent2 } from '@/shared/api/ord'
import { Input, ShadcnButton } from '@/shared/ui'

import classes from './ContragentModal.module.scss'
import { TextField, TextFieldProps } from './TextField'
import { TypeCheckboxGroup } from './TypeCheckbox'

import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/shared/ui/collapsible'
import { ChevronDown } from 'lucide-react'
import { useContragentForm, type UseContragentFormProps } from './form/hook'
import { ContragentType } from './form/schema'
import {
    DEFAULT_FORM_VALUES,
    DEFAULT_TYPE,
    makeCreateDto,
    makeFormValues,
    makeUpdateDto,
    NAME_TEXT_VARIANTS,
} from './form/values'
import { PassportField } from './PassportField'
import { PhoneField } from './PhoneField'

type ContragentFormLayoutProps = {
    children: ReactNode
    slotExtraButtons: ReactNode

    onSubmit: (e: React.SyntheticEvent) => Promise<void>
    // hasErrors: boolean
    isSubmitting: boolean
    isInnValid: boolean
}
const ContragentFormLayout = ({
    children,
    slotExtraButtons,
    onSubmit,
    // hasErrors,
    isSubmitting,
    isInnValid,
}: ContragentFormLayoutProps) => {
    const submitButton = (
        <ShadcnButton
            type="submit"
            className="w-full rounded-lg bg-blue-primary px-4 py-2 text-[13px] text-white"
            disabled={isSubmitting}
        >
            {isSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Загрузка...
                </div>
            ) : (
                'Сохранить'
            )}
        </ShadcnButton>
    )

    // if (hasErrors) {
    //     submitButton = (
    //         <DisableTooltip
    //             enabled
    //             message="Чтобы сохранить данные, нужно заполнить все поля"
    //             asChild
    //         >
    //             {submitButton}
    //         </DisableTooltip>
    //     )
    // }

    return (
        <form onSubmit={onSubmit} className={classes.form}>
            <div className={classes.fields}>{children}</div>

            {isInnValid && (
                <div className={classes.buttons}>
                    {slotExtraButtons}
                    {submitButton}
                </div>
            )}
        </form>
    )
}

type CommonFieldProps = {
    type: ContragentType
    control: ReturnType<typeof useContragentForm>['control']
    roleType?: 'publisher' | 'advertiser' | 'agency' | 'ors'
}

type TypeSpecificFormProps = CommonFieldProps & {
    nameTextPref: (typeof NAME_TEXT_VARIANTS)[ContragentType]
    RenderBankInfo: NonNullable<TextFieldProps['RenderSubscript']>
    roleType?: 'publisher' | 'advertiser' | 'agency' | 'ors'
}

const JuridicalContragentForm = ({
    type,
    control,
    nameTextPref,
    RenderBankInfo,
    roleType,
}: TypeSpecificFormProps) => {
    const common = { type, control, roleType }

    return (
        <>
            <div className="relative">
                <TextField {...common} {...nameTextPref} name="name" />
                <span className="absolute left-2 top-[30px] h-8 text-xs">
                    {nameTextPref.additionalText}
                </span>
            </div>
            <TextField
                {...common}
                withClear={true}
                name="kpp"
                label="КПП"
                placeholder="КПП"
            />
            <TextField
                {...common}
                withClear={true}
                name="ogrn"
                label="ОГРН"
                placeholder="ОГРН"
                digitsOnly={true}
            />
            <TextField
                {...common}
                withClear={true}
                name="email"
                label="E-mail"
                placeholder="Example@mail.com"
            />
            <PhoneField {...common} name="phone" label="Телефон" />
            <TextField
                {...common}
                withClear={true}
                name="legalAddress"
                label="Юридический адрес"
                placeholder="Введите адрес"
            />
            <TextField
                {...common}
                withClear={true}
                name="actualAddress"
                label="Фактический адрес"
                placeholder="Введите адрес"
            />
            <TextField
                {...common}
                withClear={true}
                name="bic"
                label="БИК"
                RenderSubscript={RenderBankInfo}
                placeholder="БИК"
            />
            <TextField
                {...common}
                label="Счет получателя"
                name="currentAccount"
                placeholder="Счет получателя"
            />
            <TextField
                {...common}
                label="Должность подписанта"
                name="position"
                placeholder="Должность"
            />
            <TextField
                {...common}
                label="ФИО подписанта"
                name="fullName"
                placeholder="ФИО"
            />
        </>
    )
}

const PhysicalContragentForm = ({
    type,
    control,
    nameTextPref,
    RenderBankInfo,
    roleType,
}: TypeSpecificFormProps) => {
    const common = { type, control, roleType }
    const isIP = type === 'ip'
    const isRequiredRole =
        roleType === 'advertiser' || roleType === 'agency' || roleType === 'ors'

    const fieldsContent = (
        <>
            <TextField
                {...common}
                withClear={true}
                name="email"
                label="E-mail"
                placeholder="Example@mail.com"
            />
            <PhoneField {...common} name="phone" label="Телефон" />
            {isIP && (
                <TextField
                    {...common}
                    withClear={true}
                    name="ogrn"
                    label="ОГРНИП"
                    placeholder="ОГРНИП"
                    digitsOnly={true}
                />
            )}
            {!isIP && (
                <PassportField
                    {...common}
                    label="Серия и номер паспорта"
                    name="passport"
                />
            )}
            <TextField
                {...common}
                withClear={true}
                name="registrationAddress"
                label="Адрес регистрации"
                placeholder="Введите адрес"
            />
            <TextField
                {...common}
                withClear={true}
                name="bic"
                label="БИК"
                RenderSubscript={RenderBankInfo}
                placeholder="БИК"
            />
            <TextField
                {...common}
                label="Счет получателя"
                name="currentAccount"
                placeholder="Счет получателя"
            />
        </>
    )

    return (
        <>
            <div className="relative">
                <TextField {...common} {...nameTextPref} name="name" />
                <span className="absolute left-2 top-[30px] h-8 text-xs">
                    {nameTextPref.additionalText}
                </span>
            </div>
            {isRequiredRole ? (
                fieldsContent
            ) : (
                <Collapsible>
                    <CollapsibleTrigger className="flex items-center pb-4 text-xs opacity-70">
                        <ChevronDown className="w-4" /> Дополнительные поля
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                        <div className={classes.fields}>{fieldsContent}</div>
                    </CollapsibleContent>
                </Collapsible>
            )}
        </>
    )
}

type ContragentFormProps = UseContragentFormProps &
    Pick<ContragentFormLayoutProps, 'slotExtraButtons'>

const ContragentForm = ({
    defaultValues,
    defaultType,
    workspace_type,
    workspace_id,
    roleType,
    onSubmitValid,
    slotExtraButtons,
}: ContragentFormProps) => {
    const {
        bankInfo,
        type,
        control,
        formState,
        onSubmit,
        onChangeType,
        isInnValid,
        optionVariant,
    } = useContragentForm({
        defaultValues,
        defaultType,
        workspace_type,
        workspace_id,
        roleType,
        onSubmitValid,
    })

    const RenderBankInfo: NonNullable<TextFieldProps['RenderSubscript']> =
        useCallback(() => {
            if (!bankInfo) {
                return undefined
            }

            return (
                <div className={classes.bankInfo}>
                    <div className={classes.bankInfoItem}>
                        <div className={classes.label}>Банк</div>
                        <Input disabled defaultValue={bankInfo.name} />
                    </div>
                    <div className={classes.bankInfoItem}>
                        <div className={classes.label}>
                            Корреспондентский счет
                        </div>
                        <Input disabled defaultValue={bankInfo.account} />
                    </div>
                </div>
            )
        }, [bankInfo])

    const nameTextPref = NAME_TEXT_VARIANTS[type]

    const isJuridical =
        type === 'juridical' || type === 'autonomous_non_profit_organization'

    const typeSpecificProps: TypeSpecificFormProps = {
        type,
        control,
        nameTextPref,
        RenderBankInfo,
        roleType,
    }

    return (
        <ContragentFormLayout
            {...{ onSubmit, slotExtraButtons }}
            isSubmitting={formState.isSubmitting}
            isInnValid={isInnValid}
        >
            <TextField
                type={type}
                control={control}
                roleType={roleType}
                label="ИНН"
                name="inn"
                digitsOnly={true}
                placeholder="ИНН"
            />
            {isInnValid && (
                <>
                    <TypeCheckboxGroup
                        optionVariant={optionVariant}
                        value={type}
                        onChange={onChangeType}
                        disabled={formState.isSubmitting}
                    />
                    {isJuridical ? (
                        <JuridicalContragentForm {...typeSpecificProps} />
                    ) : (
                        <PhysicalContragentForm {...typeSpecificProps} />
                    )}
                </>
            )}
        </ContragentFormLayout>
    )
}

export type ContragentCreateFormProps = {
    slotExtraButtons: ReactNode
    onSubmitted: (result: { status: 'success' | 'error' }) => void
    roleType?: 'publisher' | 'advertiser' | 'agency' | 'ors'
} & WithOptional<WorkspaceWithLinkDescriptor, 'link_id'>
export const ContragentCreateForm = ({
    workspace_id,
    workspace_type,
    link_id,
    slotExtraButtons,
    onSubmitted,
    roleType = 'publisher',
}: ContragentCreateFormProps) => {
    const { mutateAsync: createContragent } = useCreateContragent()

    const onSubmitValid: UseContragentFormProps['onSubmitValid'] = useCallback(
        async (formValues, type, bankInfo) => {
            try {
                await createContragent(
                    makeCreateDto(
                        {
                            workspace_id,
                            workspace_type,
                            link_id,
                        },
                        type,
                        formValues,
                        bankInfo,
                        roleType
                    )
                )
                onSubmitted({ status: 'success' })
            } catch (err: unknown) {
                console.error(err)
                onSubmitted({ status: 'error' })
            }
        },
        [
            workspace_id,
            workspace_type,
            link_id,
            createContragent,
            onSubmitted,
            roleType,
        ]
    )

    return (
        <ContragentForm
            {...{
                slotExtraButtons,
                onSubmitValid,
                workspace_id,
                workspace_type,
                roleType,
            }}
            defaultValues={DEFAULT_FORM_VALUES}
            defaultType={DEFAULT_TYPE}
        />
    )
}

export type ContragentUpdateFormProps = {
    source: ContragentResponse
    slotExtraButtons: ReactNode
    onSubmitted: (result: {
        status: 'success' | 'error' | 'not_changed'
    }) => void
    roleType?: 'publisher' | 'advertiser' | 'agency' | 'ors'
} & WithOptional<WorkspaceWithLinkDescriptor, 'link_id'>
export const ContragentUpdateForm = ({
    source,
    workspace_id,
    workspace_type,
    link_id,
    slotExtraButtons,
    onSubmitted,
    roleType = 'publisher',
}: ContragentUpdateFormProps) => {
    const { mutateAsync: updateContragent } = useUpdateContragent2()

    const onSubmitValid: UseContragentFormProps['onSubmitValid'] = useCallback(
        async (formValues, type, bankInfo) => {
            const dto = makeUpdateDto(
                {
                    workspace_id,
                    workspace_type,
                    link_id,
                },
                type,
                source,
                formValues,
                bankInfo
            )
            if (dto) {
                try {
                    await updateContragent({
                        contragentId: source.ord_contragent_id,
                        input: dto,
                    })
                    onSubmitted({ status: 'success' })
                } catch (err: unknown) {
                    console.error(err)
                }
            } else {
                onSubmitted({ status: 'not_changed' })
            }
        },
        [
            workspace_id,
            workspace_type,
            link_id,
            source,
            updateContragent,
            onSubmitted,
        ]
    )

    const defaultValues = useMemo(() => {
        return makeFormValues(source)
    }, [source])

    return (
        <ContragentForm
            {...{
                slotExtraButtons,
                onSubmitValid,
                defaultValues,
                workspace_id,
                workspace_type,
                roleType,
            }}
            defaultType={source.type}
        />
    )
}
